// The Reports screen's shared definitions (spec/17 §3 M-01, M-19; spec/13 §15; F-ANL-01, F-ANL-03): the **one
// revenue definition** every report reads — the Reports tabs, the organisation's roll-up and the day close — and the
// pure date maths of a report's range (presets, the previous period, deltas).
//
// **Light on purpose.** The console imports this file through the narrow sub-path `@clavian/domain/reports`. It
// imports only `./ledger-metrics.ts` (pure, no contracts runtime), so no zod-openapi `new Function` reaches the
// console's bundle (its `script-src` forbids eval; `apps/console/__tests__/pages-csp.test.ts`).
//
// ## Revenue, in plain words (the owner's rule, 2026-10-02)
//
//   - Money a player **spends from their wallet** — on play time (pay as you go), food and drink, or a pass — **is
//     revenue**, on the day it is spent. It is counted by tender as `wallet`.
//   - A wallet **top-up is not revenue**: it is money the venue holds for the player (a liability) until it is spent.
//     It is "money in", never revenue. Handing a top-up back is not a refund of revenue either.
//   - **Bonus credit** spent (the free credit given with a top-up, a coupon or a manual credit) is **not revenue**: it
//     is a promotional cost, shown on its own (`bonusSpent`).
//   - Cash, card, UPI and online payments for a sale, a pass or play are revenue when settled; on account is revenue
//     when the tab is settled; refunds come off.
//
// That is exactly M-01 (`revenueOf`): the payment accounts are the venue's tenders **and the player's `wallet_cash`**,
// on every entry but a top-up, its hand-back, a wallet adjustment and a provider payout. `revenueBreakdownOf` adds the
// figures a report shows beside it, from the same cells, and they reconcile exactly:
//
//   revenue = moneyIn − walletTopUps + walletHandedBack + walletSpent
//
// because a top-up's tender debit is money in but not revenue, a hand-back's tender credit is money out but not a
// refund, and the wallet's spend is revenue that came in as money on an earlier day. The day close's "From sales to
// revenue" bridge (`dayRevenueBridgeOf`) is the other reconciliation of the same `revenue`, from the sales side.
import { ONLINE_PAYMENT_FEES_ENTRY_KIND, ONLINE_REFUND_FEES_ENTRY_KIND, PAYMENT_ACCOUNT_KINDS, REVENUE_EXCLUDED_ENTRY_KINDS, REVENUE_TENDERS, revenueOf, } from "./ledger-metrics.js";
// ---------------------------------------------------------------------------------------------
// The revenue breakdown
// ---------------------------------------------------------------------------------------------
/** The tenders money reaches the business through from outside it (every revenue tender but the wallet). */
export const MONEY_IN_TENDERS = ['cash', 'counter_card', 'counter_upi', 'online'];
/** Entries that move money between the business's own accounts: never money in (a payout, a manual correction). */
const NOT_MONEY_IN_ENTRY_KINDS = new Set([
    'provider_settlement',
    'wallet_adjustment',
    // An online payment's commission and fee (ADR-0061): the cost of the money in, never less money in.
    ONLINE_PAYMENT_FEES_ENTRY_KIND,
    ONLINE_REFUND_FEES_ENTRY_KIND,
]);
/**
 * Entries that put credit the venue gives away into a wallet: a top-up bonus, a coupon's wallet grant, a manual credit.
 * Bonus granted is read from them; bonus **spent** is the bonus wallet's debit on every other entry.
 */
const BONUS_CREDIT_ENTRY_KINDS = new Set(['topup_bonus', 'coupon_grant', 'wallet_adjustment']);
const EXTERNAL_ACCOUNTS = new Map(MONEY_IN_TENDERS.map((tender) => [PAYMENT_ACCOUNT_KINDS[tender], tender]));
const zeroTenders = () => ({ cash: 0n, counter_card: 0n, counter_upi: 0n, online: 0n, wallet: 0n });
/**
 * **The revenue definition** with its money-in reconciliation (see the file header). `null` when the window touched
 * no payment account and no wallet: a report then shows "no data", never a `0` it did not measure (spec/17 §1.5).
 *
 * ```ts
 * // a ₹1,000 cash top-up, ₹300 of it spent on play from the wallet:
 * revenueBreakdownOf([
 *   { entryKind: 'wallet_topup', accountKind: 'tender_cash', ownerType: 'venue', debit: 100000n, credit: 0n },
 *   { entryKind: 'wallet_topup', accountKind: 'wallet_cash', ownerType: 'player', debit: 0n, credit: 100000n },
 *   { entryKind: 'segment_charge', accountKind: 'wallet_cash', ownerType: 'player', debit: 30000n, credit: 0n },
 *   { entryKind: 'segment_charge', accountKind: 'sales_play', ownerType: 'venue', debit: 0n, credit: 30000n },
 * ]) // revenue 30000n (the spend), moneyIn 100000n (the top-up), walletTopUps 100000n, walletSpent 30000n
 * ```
 */
export function revenueBreakdownOf(cells) {
    const revenue = revenueOf(cells);
    const moneyInBySource = { cash: 0n, counter_card: 0n, counter_upi: 0n, online: 0n };
    let walletTopUps = 0n;
    let walletHandedBack = 0n;
    let bonusSpent = 0n;
    let bonusGranted = 0n;
    let seen = revenue !== null;
    for (const cell of cells) {
        const tender = EXTERNAL_ACCOUNTS.get(cell.accountKind);
        if (tender !== undefined && !NOT_MONEY_IN_ENTRY_KINDS.has(cell.entryKind)) {
            seen = true;
            moneyInBySource[tender] += cell.debit - cell.credit;
            if (cell.entryKind === 'wallet_topup')
                walletTopUps += cell.debit;
            if (cell.entryKind === 'wallet_refund')
                walletHandedBack += cell.credit;
        }
        if (cell.accountKind === 'wallet_bonus') {
            seen = true;
            if (BONUS_CREDIT_ENTRY_KINDS.has(cell.entryKind))
                bonusGranted += cell.credit - cell.debit;
            else if (!REVENUE_EXCLUDED_ENTRY_KINDS.has(cell.entryKind))
                bonusSpent += cell.debit - cell.credit;
        }
        if (cell.accountKind === 'wallet_cash' || cell.accountKind === 'wallet_cash_tax_paid')
            seen = true;
    }
    if (!seen)
        return null;
    const bySource = revenue === null ? zeroTenders() : { ...revenue.byTender };
    return {
        revenue: revenue?.total ?? 0n,
        hasRevenue: revenue !== null,
        payments: revenue?.payments ?? 0n,
        refunds: revenue?.refunds ?? 0n,
        bySource,
        walletSpent: bySource.wallet,
        moneyIn: MONEY_IN_TENDERS.reduce((sum, key) => sum + moneyInBySource[key], 0n),
        moneyInBySource,
        walletTopUps,
        walletHandedBack,
        bonusSpent,
        bonusGranted,
    };
}
/** Several windows' breakdowns as one (a range of days, or venues rolled up). `null` when every part is. */
export function sumRevenueBreakdowns(parts) {
    const present = parts.filter((part) => part !== null);
    if (present.length === 0)
        return null;
    const add = (pick, keys) => {
        const out = {};
        for (const key of keys)
            out[key] = present.reduce((sum, part) => sum + pick(part)[key], 0n);
        return out;
    };
    const total = (pick) => present.reduce((sum, part) => sum + pick(part), 0n);
    return {
        revenue: total((part) => part.revenue),
        hasRevenue: present.some((part) => part.hasRevenue),
        payments: total((part) => part.payments),
        refunds: total((part) => part.refunds),
        bySource: add((part) => part.bySource, REVENUE_TENDERS),
        walletSpent: total((part) => part.walletSpent),
        moneyIn: total((part) => part.moneyIn),
        moneyInBySource: add((part) => part.moneyInBySource, MONEY_IN_TENDERS),
        walletTopUps: total((part) => part.walletTopUps),
        walletHandedBack: total((part) => part.walletHandedBack),
        bonusSpent: total((part) => part.bonusSpent),
        bonusGranted: total((part) => part.bonusGranted),
    };
}
/** Average revenue per active player (M-01 ÷ M-06), rounded half up; `null` without revenue or players. */
export function revenuePerPlayerOf(revenue, activePlayers) {
    if (revenue === null || activePlayers === null || activePlayers <= 0)
        return null;
    const players = BigInt(activePlayers);
    const doubled = revenue * 2n;
    // Half up, symmetric for a negative revenue.
    return doubled >= 0n ? (doubled + players) / (players * 2n) : -((-doubled + players) / (players * 2n));
}
// ---------------------------------------------------------------------------------------------
// A report's range: presets, the previous period, deltas
// ---------------------------------------------------------------------------------------------
/** The longest range a report reads, in business days (a quarter, as the overview). */
export const REPORT_RANGE_MAX_DAYS = 92;
/** The ranges a report offers by name; anything else is a custom `from` … `to`. */
export const REPORT_PRESETS = ['today', 'yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth'];
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Whether `value` is a real `YYYY-MM-DD` date. */
export function isReportDate(value) {
    const match = DATE.exec(value);
    if (match === null)
        return false;
    const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return at.toISOString().slice(0, 10) === value;
}
/** `date` moved by `days` calendar days. Business days are named by their date, so this is how they step. */
export function shiftReportDate(date, days) {
    const match = DATE.exec(date);
    if (match === null)
        throw new RangeError(`Not a date: "${date}".`);
    const at = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) + days * 86_400_000;
    return new Date(at).toISOString().slice(0, 10);
}
/** How many business days `from` … `to` covers (inclusive); 0 when `to` is before `from`. */
export function reportRangeDays(range) {
    if (range.to < range.from)
        return 0;
    return Math.round((Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / 86_400_000) + 1;
}
/** Whether `value` names a preset. */
export function isReportPreset(value) {
    return REPORT_PRESETS.includes(value);
}
/**
 * The business days a preset covers, given the venue's business day **today** (its timezone and cutoff decide it —
 * the server reads it, `venueDayOf`). Months are the months the business days are dated in.
 */
export function reportRangeOf(preset, today) {
    switch (preset) {
        case 'today':
            return { from: today, to: today };
        case 'yesterday': {
            const day = shiftReportDate(today, -1);
            return { from: day, to: day };
        }
        case 'last7':
            return { from: shiftReportDate(today, -6), to: today };
        case 'last30':
            return { from: shiftReportDate(today, -29), to: today };
        case 'thisMonth':
            return { from: `${today.slice(0, 8)}01`, to: today };
        case 'lastMonth': {
            const lastOfPrevious = shiftReportDate(`${today.slice(0, 8)}01`, -1);
            return { from: `${lastOfPrevious.slice(0, 8)}01`, to: lastOfPrevious };
        }
    }
}
/** The period a range is compared with: as many business days, ending the day before it starts. */
export function previousRangeOf(range) {
    const days = reportRangeDays(range);
    return { from: shiftReportDate(range.from, -days), to: shiftReportDate(range.from, -1) };
}
/**
 * The delta of a figure against the previous period's. `null` when either side has no data — a comparison with
 * "nothing measured" is not a 100 % rise (spec/17 §1.5).
 */
export function reportDeltaOf(current, previous) {
    if (current === null || current === undefined || previous === null || previous === undefined)
        return null;
    const change = current - previous;
    const basisPoints = previous === 0 ? null : Math.sign(change) * Math.round((Math.abs(change) * 10_000) / Math.abs(previous));
    return { change, basisPoints, trend: change > 0 ? 'up' : change < 0 ? 'down' : 'flat' };
}
// ---------------------------------------------------------------------------------------------
// Play sold: as passes, or as play time at the rate card
// ---------------------------------------------------------------------------------------------
/** Entries that sell play as a pass: a pass sold on its own, a sale's time line, and their refunds. */
const PASS_PLAY_ENTRY_KINDS = new Set(['pass_sale', 'sale_settlement', 'pass_refund', 'refund']);
/** Entries that charge play time at the rate card (pay as you go, from a wallet, on account) and their reversals. */
const RATE_CARD_PLAY_ENTRY_KINDS = new Set(['segment_charge', 'segment_charge_reversal']);
/** `sales_play`'s net credit split into passes and rate-card play time (the Products tab, F-ANL-03). */
export function playRevenueOf(cells) {
    let passes = 0n;
    let rateCard = 0n;
    let discounts = 0n;
    for (const cell of cells) {
        if (cell.accountKind === 'sales_play') {
            if (PASS_PLAY_ENTRY_KINDS.has(cell.entryKind))
                passes += cell.credit - cell.debit;
            else if (RATE_CARD_PLAY_ENTRY_KINDS.has(cell.entryKind))
                rateCard += cell.credit - cell.debit;
        }
        else if (cell.accountKind === 'discounts' && RATE_CARD_PLAY_ENTRY_KINDS.has(cell.entryKind)) {
            discounts += cell.debit - cell.credit;
        }
    }
    return { passes, rateCard, discounts };
}
//# sourceMappingURL=reports.js.map